// frontend/src/components/team/WorkflowRunHistoryDrawer.tsx
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Drawer,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import React from 'react';
import type { Workflow, WorkflowRun } from '../../services/api';
import { workflowsApi } from '../../services/api';

const STATUS_COLORS: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  completed: 'success',
  failed: 'error',
  running: 'warning',
  pending: 'default',
};

interface Props {
  open: boolean;
  onClose: () => void;
  workflow: Workflow | null;
  teamId: string;
}

const WorkflowRunHistoryDrawer: React.FC<Props> = ({ open, onClose, workflow, teamId }) => {
  const { data: runs = [], isLoading } = useQuery({
    queryKey: ['workflow-runs', workflow?.id],
    queryFn: () =>
      workflow ? workflowsApi.listRuns(teamId, workflow.id).then((r) => r.data) : [],
    enabled: open && !!workflow,
    refetchInterval: 5000,
  });

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 480, p: 2 } }}
    >
      <Typography variant="h6" mb={2}>
        {workflow?.name} — Run History
      </Typography>

      {isLoading && <Typography color="text.secondary">Loading...</Typography>}
      {!isLoading && runs.length === 0 && (
        <Typography color="text.secondary">No runs yet.</Typography>
      )}

      {runs.map((run: WorkflowRun) => (
        <Box key={run.id} mb={1.5}>
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <Chip
              label={run.status.toUpperCase()}
              size="small"
              color={STATUS_COLORS[run.status] ?? 'default'}
            />
            <Chip label={run.triggered_by} size="small" variant="outlined" />
            <Typography variant="caption" color="text.secondary">
              {run.started_at
                ? formatDistanceToNow(new Date(run.started_at), { addSuffix: true })
                : '—'}
            </Typography>
          </Box>

          {run.output_text && (
            <Typography variant="body2" sx={{ mb: 0.5, whiteSpace: 'pre-wrap' }}>
              {run.output_text}
            </Typography>
          )}
          {run.error_msg && (
            <Typography variant="caption" color="error.main">
              {run.error_msg}
            </Typography>
          )}

          {run.steps.length > 0 && (
            <Accordion disableGutters elevation={0} variant="outlined" sx={{ mt: 0.5 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="caption">{run.steps.length} step(s)</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 1 }}>
                {run.steps.map((step, i) => (
                  <Box key={i} mb={1}>
                    <Typography variant="caption" fontWeight={600}>
                      {i + 1}. {step.tool}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      In: {step.input_summary}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      Out: {step.output_summary}
                    </Typography>
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      ))}
    </Drawer>
  );
};

export default WorkflowRunHistoryDrawer;
